import React, { createContext, useCallback, useEffect, useRef, useState, useContext, useReducer } from "react";
import { db } from "../firebase";
import { FB_COLLECTION_PATH } from "../functions/postActionTypes";
import { AuthContext } from "./AuthContext";
import { userInfoFormatting } from "../utils/userInfoFormatting";
import * as Sentry from "@sentry/react";
import {
  query,
  collection,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  collectionGroup,
  orderBy,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { ASSOCIATESTATUS_TYPES, WORKFLOW_WERKER_TYPES } from "../functions/postActionTypes";
import { SUPPORT_MONKEY_UID } from "../constants/supportConstants";
import packageJson from "../../package.json";
import { Capacitor } from "@capacitor/core";
import { LOG_LEVEL, Purchases } from "@revenuecat/purchases-capacitor";
import { Badge } from "@capawesome/capacitor-badge";
import { App } from "@capacitor/app";
import { Network } from "@capacitor/network";
import { useIonToast } from "@ionic/react";

// ==================== Type Definitions ====================
interface ProfileData {
  UserId: string;
  FirstName: string;
  LastName: string;
  Email: string;
  Username: string;
  IsScheduler: boolean;
  IsDeleted: boolean;
  Company: string;
  Occupation: string;
  ProfilePicURL: string;
  UserBio: string;
  DocPath: string;
  Associates: [string];
  UserLastRoleScheduler: string;
  SubscriptionType: string;
  SubscriptionExpiration: number;
  OnboardingWerkerSeen: boolean;
  OnboardingSchedulerSeen: boolean;
}

export interface AssociatesData {
  WerkerUid: string;
  WerkerFirstName: string;
  WerkerLastName: string;
  WerkerProfilePicURL: string;
  WerkerCompany: string;
  Occupation: string;
  WerkerBio: string;
  AssociateStatus: string;
  isRead_Request: boolean;
}

interface MyChats {
  DMuid: string;
  IsRead: boolean;
  MessageDate: string;
  MessagePreview: string;
  MessageTitle: string;
  SendersPic: string;
  Key: string;
}

interface AnnouncementFeedItem {
  announcementId: string;
  title: string;
  bodyPreview: string;
  authorUid: string;
  authorName: string;
  authorCompany: string;
  authorProfilePicURL?: string;
  imageUrl: string;
  createdAt: number;
  isPinned: boolean;
  isRead: boolean;
  readAt?: number | null;
  status?: string;
}

interface MyAnnouncementPost {
  announcementId: string;
  authorUid: string;
  authorName: string;
  authorCompany: string;
  authorProfilePicURL?: string;
  title: string;
  body: string;
  imageUrl: string | null;
  createdAt: number;
  updatedAt?: number;
  isPinned: boolean;
  status?: string;
  recipientUids?: string[];
  selectedCrewIds?: string[];
}

type JobRouteMeta = {
  myJobDocId: string;
  schedUid: string;
};

export const ProfileContext = createContext<any>({});

const COMP_SCHEDULER_EXPIRY = new Date(2099, 0, 1).getTime();

export const ProfileContextProvider: React.FC<any> = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const [presentToast] = useIonToast();
  const [profilePicBadge, setProfilePicBadge] = useState(0);
  const [badgeCountAssociateRequestsContext, setBadgeCountAssociateRequestsContext] = useState(0);
  const [badgeCountNotificationsContext, setBadgeCountNotificationsContext] = useState(0);
  const [badgeCountDMContext, setBadgeCountDMContext] = useState(0);
  const [badgeCountPaymentReceiptsContext, setBadgeCountPaymentReceiptsContext] = useState(0);
  const [networkConnection, setNetworkConnection] = useState(true);
  const [colorTheme, setColorTheme] = useState("bckgrnd");
  const [buttonColor, setButtonColor] = useState("medium");
  const [usersID, setUsersID] = useState("");
  // const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [subscriptionRefreshCtr, setSubscriptionRefreshCtr] = useState(0);

  const [profile, setProfile] = useState<ProfileData>({
    UserId: "",
    FirstName: "",
    LastName: "",
    Email: "",
    Username: "",
    IsScheduler: false,
    IsDeleted: false,
    Company: "",
    Occupation: "",
    ProfilePicURL: "",
    UserBio: "Hello!",
    DocPath: "",
    Associates: [""],
    UserLastRoleScheduler: "",
    SubscriptionType: "",
    SubscriptionExpiration: 0,
    OnboardingWerkerSeen: false,
    OnboardingSchedulerSeen: false,
  });

  const [myAssociates, setMyAssociates] = React.useState<AssociatesData[]>([
    {
      WerkerUid: "",
      WerkerFirstName: "",
      WerkerLastName: "",
      WerkerCompany: "",
      Occupation: "",
      WerkerProfilePicURL: "../assets/profilePic.png",
      WerkerBio: "",
      AssociateStatus: "",
      isRead_Request: true,
    },
  ]);
  const [myRequests, setMyRequests] = React.useState<AssociatesData[]>([
    {
      WerkerUid: "",
      WerkerFirstName: "",
      WerkerLastName: "",
      WerkerCompany: "",
      Occupation: "",
      WerkerProfilePicURL: "../assets/profilePic.png",
      WerkerBio: "",
      AssociateStatus: "",
      isRead_Request: true,
    },
  ]);
  const [everyoneElse, setEveryoneElse] = React.useState<AssociatesData[]>([
    {
      WerkerUid: "",
      WerkerFirstName: "",
      WerkerLastName: "",
      WerkerCompany: "",
      Occupation: "",
      WerkerProfilePicURL: "../assets/profilePic.png",
      WerkerBio: "",
      AssociateStatus: "",
      isRead_Request: true,
    },
  ]);

  const [myChats, setMyChats] = React.useState<MyChats[]>([
    {
      DMuid: "",
      IsRead: true,
      MessageDate: "",
      MessagePreview: "",
      MessageTitle: "",
      SendersPic: "",
      Key: "",
    },
  ]);

  const [myAnnouncements, setMyAnnouncements] = React.useState<AnnouncementFeedItem[]>([]);
  const [badgeCountAnnouncementsContext, setBadgeCountAnnouncementsContext] = useState(0);

  const [myAnnouncementPosts, setMyAnnouncementPosts] = React.useState<MyAnnouncementPost[]>([]);

  // ---- Job chat allow/deny + routing meta (shared with Chats + Tab badge) ----
  const [allowedJobIdsWerker, setAllowedJobIdsWerker] = React.useState<Set<string>>(new Set());
  const [allowedJobIdsScheduler, setAllowedJobIdsScheduler] = React.useState<Set<string>>(new Set());
  const [jobRouteByJobId, setJobRouteByJobId] = React.useState<Record<string, JobRouteMeta>>({});

  const [werkerJobsReady, setWerkerJobsReady] = React.useState(false);
  const [schedulerJobsReady, setSchedulerJobsReady] = React.useState(false);

  const [cancelledBySchedulerWerker, setCancelledBySchedulerWerker] = React.useState<Record<string, boolean>>({});
  const [cancelledBySchedulerScheduler, setCancelledBySchedulerScheduler] = React.useState<Record<string, boolean>>({});

  const allowedJobIdsUnion = React.useMemo(() => {
    const out = new Set<string>();
    allowedJobIdsWerker.forEach((x) => out.add(x));
    allowedJobIdsScheduler.forEach((x) => out.add(x));
    return out;
  }, [allowedJobIdsWerker, allowedJobIdsScheduler]);

  const jobFilteringReady = React.useMemo(
    () => werkerJobsReady && schedulerJobsReady,
    [werkerJobsReady, schedulerJobsReady]
  );

  // -----------------------------
  // Network connectivity tracking (Capacitor Network plugin)
  // -----------------------------
  useEffect(() => {
    let cancelled = false;

    Network.getStatus()
      .then((status) => {
        if (!cancelled) setNetworkConnection(status.connected);
      })
      .catch(() => {
        /* ignore - keep optimistic default */
      });

    const handlePromise = Network.addListener("networkStatusChange", (status) => {
      setNetworkConnection(status.connected);
    });

    return () => {
      cancelled = true;
      handlePromise.then((h) => h.remove()).catch(() => {});
      Network.removeAllListeners();
    };
  }, []);

  // -----------------------------
  // Werker-side allowed jobs + route meta
  // Invited / Scheduled / Past only
  // -----------------------------
  React.useEffect(() => {
    const uid = profile?.UserId;
    if (!uid) return;

    setWerkerJobsReady(false);

    const werkerQ = query(
      collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
      where("docStamp.userInfoTo.uid", "==", uid),
      where("jobInfo.status.jobStatusWerker", "in", [
        WORKFLOW_WERKER_TYPES.Invited,
        WORKFLOW_WERKER_TYPES.Scheduled,
        WORKFLOW_WERKER_TYPES.Past,
      ])
    );

    const unsub = onSnapshot(
      werkerQ,
      (snap) => {
        const allowed = new Set<string>();
        const routeMap: Record<string, JobRouteMeta> = {};
        const cancelledMap: Record<string, boolean> = {};

        snap.forEach((docSnap) => {
          const data: any = docSnap.data();

          const jobId = String(data?.jobInfo?.core?.uniqueJobId || "");
          if (!jobId) return;

          allowed.add(jobId);

          const schedUid = String(data?.docStamp?.userInfoFrom?.uid || "");
          routeMap[jobId] = {
            myJobDocId: docSnap.id,
            schedUid,
          };

          const cancelled = data?.jobInfo?.status?.jobCancelledByScheduler === true;
          cancelledMap[jobId] = cancelled;
        });

        setAllowedJobIdsWerker(allowed);
        setCancelledBySchedulerWerker(cancelledMap);

        // merge route meta (don't wipe scheduler-derived meta)
        setJobRouteByJobId((prev) => ({ ...prev, ...routeMap }));

        setWerkerJobsReady(true);
      },
      (_err) => {
        // console.log(err);
        setAllowedJobIdsWerker(new Set());
        setCancelledBySchedulerWerker({});
        setWerkerJobsReady(true);
      }
    );

    return () => unsub();
  }, [profile?.UserId]);

  // -----------------------------
  // Scheduler-side allowed jobs
  // Filter OUT cancelled/expired/deleted/etc using client-side check.
  // Query self-docs (from==to==me), then exclude bad ones.
  // -----------------------------
  React.useEffect(() => {
    const uid = profile?.UserId;
    if (!uid) return;

    setSchedulerJobsReady(false);

    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
    const twelveMonthsAgoISO = twelveMonthsAgo.toISOString().substring(0, 19);

    const schedQ = query(
      collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
      where("docStamp.userInfoFrom.uid", "==", uid),
      where("docStamp.userInfoTo.uid", "==", uid),
      where("jobInfo.core.dateTimeStart", ">=", twelveMonthsAgoISO),
      orderBy("jobInfo.core.dateTimeStart", "desc")
    );

    const unsub = onSnapshot(
      schedQ,
      (snap) => {
        const allowed = new Set<string>();
        const cancelledMap: Record<string, boolean> = {};

        snap.forEach((docSnap) => {
          const data: any = docSnap.data();

          const jobId = String(data?.jobInfo?.core?.uniqueJobId || "");
          if (!jobId) return;

          const cancelled = data?.jobInfo?.status?.jobCancelledByScheduler === true;

          const statusMap = data?.jobInfo?.status || {};
          const jobStatusSched = String(statusMap?.jobStatusSched || "").toLowerCase();
          const jobStatusWerker = String(statusMap?.jobStatusWerker || "").toLowerCase();

          const looksDeleted = jobStatusSched.includes("delete") || jobStatusWerker.includes("delete");
          const looksExpired = jobStatusSched.includes("expire") || jobStatusWerker.includes("expire");

          if (!looksDeleted && !looksExpired) {
            allowed.add(jobId);
          }

          cancelledMap[jobId] = cancelled;
        });

        setAllowedJobIdsScheduler(allowed);
        setCancelledBySchedulerScheduler(cancelledMap);
        setSchedulerJobsReady(true);
      },
      (_err) => {
        // console.log(err);
        setAllowedJobIdsScheduler(new Set());
        setCancelledBySchedulerScheduler({});
        setSchedulerJobsReady(true);
      }
    );

    return () => {
      unsub();
    };
  }, [profile.UserId, profile.Associates]);

  // Get list of Associate Requests
  useEffect(() => {
    if (profile.UserId === "") return;

    let isMounted = true;

    const unsubscribe = onSnapshot(doc(db, FB_COLLECTION_PATH.UserAssociations, profile.UserId), async (docu) => {
      var associateStatusArray: any[] = [];
      var userArray: any = [];
      try {
        const associateAttributes = docu.data();
        for (const key in associateAttributes) {
          if (key !== "myDoc") {
            if (
              associateAttributes[key].associateStatus === ASSOCIATESTATUS_TYPES.RequestSent ||
              associateAttributes[key].associateStatus === ASSOCIATESTATUS_TYPES.RequestReceived
            ) {
              const associateObject = {
                Uid: key,
                AssociateStatus: associateAttributes[key].associateStatus,
                isRead_Request: associateAttributes[key].isRead,
              };
              associateStatusArray = [...associateStatusArray, associateObject];
            }
          }
        }
        const promises = associateStatusArray.map(async (details: any) => {
          const userDocRef = doc(db, FB_COLLECTION_PATH.Users, details.Uid);
          const userDocSnap = await getDoc(userDocRef);
          const user = userDocSnap.data();
          const userDetail = userInfoFormatting(user, details);
          userArray.push(userDetail);
          return { ...user };
        });
        await Promise.all(promises);
        if (isMounted) {
          setMyRequests(userArray);
        }
      } catch (error) {
        // console.log(error);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [profile.UserId]);

  // Get list of Associates (confirmed, not pending requests)
  useEffect(() => {
    if (profile.UserId === "") return;

    let isMounted = true;

    const unsubscribe = onSnapshot(doc(db, FB_COLLECTION_PATH.UserAssociations, profile.UserId), async (docu) => {
      var associateStatusArray: any[] = [];
      var userArray: any = [];
      try {
        const associateAttributes = docu.data();
        for (const key in associateAttributes) {
          if (key !== "myDoc") {
            if (associateAttributes[key].associateStatus === ASSOCIATESTATUS_TYPES.Associate) {
              const associateObject = {
                Uid: key,
                AssociateStatus: associateAttributes[key].associateStatus,
                isRead_Request: associateAttributes[key].isRead,
              };
              associateStatusArray = [...associateStatusArray, associateObject];
            }
          }
        }
        const promises = associateStatusArray.map(async (details: any) => {
          const userDocRef = doc(db, FB_COLLECTION_PATH.Users, details.Uid);
          const userDocSnap = await getDoc(userDocRef);
          const user = userDocSnap.data();
          const userDetail = userInfoFormatting(user, details);
          userArray.push(userDetail);
          return { ...user };
        });
        await Promise.all(promises);
        if (isMounted) {
          setMyAssociates(userArray);
        }
      } catch (error) {
        // console.log(error);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [profile.UserId]);

  // Badge count for incoming associate requests
  useEffect(() => {
    let count = 0;
    myRequests.forEach((associate: any) => {
      if (
        associate.AssociateStatus === ASSOCIATESTATUS_TYPES.RequestReceived &&
        associate.isRead_Request === false
      ) {
        count++;
      }
    });
    setBadgeCountAssociateRequestsContext(count);
  }, [myRequests]);

  // Refs so refreshEveryoneElse can read the latest lists without being recreated
  const myAssociatesRef = useRef<AssociatesData[]>([]);
  const myRequestsRef = useRef<AssociatesData[]>([]);
  useEffect(() => { myAssociatesRef.current = myAssociates; }, [myAssociates]);
  useEffect(() => { myRequestsRef.current = myRequests; }, [myRequests]);

  // Replaces the former real-time listener on the full user table.
  // Called explicitly by the Associates page on entry so the listener
  // only runs when the user actually navigates to that screen.
  const refreshEveryoneElse = useCallback(async () => {
    const uid = profile?.UserId;
    if (!uid) return;
    try {
      const snap = await getDocs(query(collectionGroup(db, FB_COLLECTION_PATH.UsersPublicFacing)));
      const knownUids = new Set<string>([
        ...myAssociatesRef.current.map((a: AssociatesData) => a.WerkerUid),
        ...myRequestsRef.current.map((r: AssociatesData) => r.WerkerUid),
      ]);
      const arr: any[] = [];
      snap.forEach((docSnap: any) => {
        const userAttributes = docSnap.data();
        if (docSnap.id !== uid && !knownUids.has(userAttributes.uid)) {
          arr.push(
            userInfoFormatting(userAttributes, {
              AssociateStatus: "Everyone",
              Uid: "",
              isRead_Request: true,
            })
          );
        }
      });
      setEveryoneElse(arr);
    } catch {
      // no-op
    }
  }, [profile?.UserId]);

  useEffect(() => {
    let listenerHandle: any;

    const clearNativeBadge = async () => {
      try {
        await Badge.set({ count: 0 });
      } catch (err) {
        // console.log("Badge clear failed", err);
      }
    };

    const clearFirestoreBadge = async () => {
      if (!currentUser?.uid) return;
      try {
        const badgeRef = doc(db, `users/${currentUser.uid}/users_BadgeCount`, currentUser.uid);
        const snap = await getDoc(badgeRef);
        if (snap.exists() && (snap.data()?.badgeCount || 0) > 0) {
          await setDoc(badgeRef, { badgeCount: 0 }, { merge: true });
        }
      } catch (err) {
        // console.log("Firestore badge clear failed", err);
      }
    };

    // Update presence document to track app active state
    const updatePresence = async (isActive: boolean) => {
      if (!currentUser?.uid) return;
      try {
        const presenceRef = doc(db, `users/${currentUser.uid}/presence`, currentUser.uid);
        
        // Detect platform
        let platform = "web";
        if (Capacitor.isNativePlatform()) {
          platform = Capacitor.getPlatform(); // "ios" or "android"
        }
        
        await setDoc(presenceRef, {
          isActive: isActive,
          lastActivity: Date.now(),
          platform: platform,
        }, { merge: true });
        
        Sentry.addBreadcrumb({ 
          category: "presence", 
          message: `Presence updated: ${isActive ? "active" : "inactive"}`, 
          level: "info" 
        });
      } catch (err) {
        // console.log("Presence update failed", err);
      }
    };

    const clearAllBadges = async () => {
      await clearNativeBadge();
      await clearFirestoreBadge();
    };

    const setupListener = async () => {
      // Clear badges and set presence to active on app start
      await clearAllBadges();
      await updatePresence(true);

      listenerHandle = await App.addListener("appStateChange", ({ isActive }) => {
        Sentry.addBreadcrumb({ category: "app.lifecycle", message: isActive ? "App resumed (foreground)" : "App backgrounded", level: "info" });
        
        if (isActive) {
          clearAllBadges();
          updatePresence(true);
        } else {
          updatePresence(false);
        }
      });
    };

    setupListener();

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [currentUser]);

  // Chats listener /////
  React.useEffect(() => {
    if (profile.UserId !== "") {
      try {
        const unsubscribe3 = onSnapshot(doc(db, FB_COLLECTION_PATH.Chats, profile.UserId), (msgDoc) => {
          let unreadCounter = 0;
          const msgAttributes: any = msgDoc.data() || {};
          const tempChats: any[] = [];
          // Build associate set (same rule as Chats page)
          const associateUidSet = new Set<string>();
          (myAssociates || []).forEach((a: any) => {
            if (a?.WerkerUid) associateUidSet.add(a.WerkerUid);
          });
          const isUnstartedChat = (preview: any) => {
            const p = String(preview || "")
              .trim()
              .toLowerCase();
            return p === "chat created" || p.trim() === "";
          };
          for (const key in msgAttributes) {
            const row = msgAttributes[key];
            const DMuid = row?.DMuid || "";

            const chatObj = {
              Key: key,
              IsRead: row?.isRead,
              MessageDate: row?.messageDate,
              MessagePreview: row?.messagePreview,
              MessageTitle: row?.messageTitle,
              SendersPic: row?.sendersPic,
              DMuid,
            };
            tempChats.push(chatObj);
            // ---- Badge counting mirrors Chats page exclusions ----
            const unread = row?.isRead === false;
            if (!unread) continue;

            const preview = row?.messagePreview;
            if (isUnstartedChat(preview)) continue;

            const isJobChat = typeof DMuid === "string" && String(DMuid).trim() === "";
            const isDmChat = typeof DMuid === "string" && String(DMuid).trim() !== "";

            if (isDmChat) {
              // DM must be an associate, EXCEPT support monkey always counts
              const dmuidStr = String(DMuid);
              const isSupport = dmuidStr === SUPPORT_MONKEY_UID;

              if (isSupport || associateUidSet.has(dmuidStr)) {
                unreadCounter += 1;
              }
              continue;
            }
            if (isJobChat) {
              // Match Chats page: hide job chats until job filtering is ready
              if (!jobFilteringReady) continue;
              const jobId = String(key || "");
              if (!jobId) continue;
              // Must be allowed job chat
              if (!allowedJobIdsUnion.has(jobId)) continue;
              // Cancelled+read is hidden in Chats page, but this one is unread (we already checked),
              // so it should still count (and still show on the list) -> no extra cancelled check needed.
              unreadCounter += 1;
              continue;
            }
          }
          setMyChats(tempChats);
          // This drives the Message tab badge.
          // (Yes, the name is legacy — now it's total unread chats visible in Chats page + support.)
          setBadgeCountDMContext(unreadCounter);
        });
        return () => {
          unsubscribe3();
        };
      } catch (error) {
        // console.log(error);
      }
    }
  }, [profile.UserId, myAssociates, jobFilteringReady, allowedJobIdsUnion]);

  React.useEffect(() => {
    if (profile.UserId !== "") {
      try {
        const announcementFeedRef = collection(db, FB_COLLECTION_PATH.Users, profile.UserId, FB_COLLECTION_PATH.AnnouncementFeed);

        const unsubscribeAnnouncements = onSnapshot(
          announcementFeedRef,
          (querySnapshot) => {
            const tempAnnouncements: AnnouncementFeedItem[] = [];
            let unreadCounter = 0;

            querySnapshot.forEach((docSnap) => {
              const data: any = docSnap.data() || {};

              const announcementObj: AnnouncementFeedItem = {
                announcementId: docSnap.id,
                title: data?.title || "",
                bodyPreview: data?.bodyPreview || "",
                authorUid: data?.authorUid || "",
                authorName: data?.authorName || "",
                authorCompany: data?.authorCompany || "",
                authorProfilePicURL: data?.authorProfilePicURL || "../assets/profilePic.png",
                imageUrl: data?.imageUrl || "",
                createdAt: Number(data?.createdAt) || 0,
                isPinned: data?.isPinned === true,
                isRead: data?.isRead === true,
                readAt: data?.readAt === undefined || data?.readAt === null ? null : Number(data?.readAt) || null,
                status: data?.status || "active",
              };

              if (announcementObj.status !== "deleted") {
                tempAnnouncements.push(announcementObj);

                if (announcementObj.isRead === false) {
                  unreadCounter += 1;
                }
              }
            });

            setMyAnnouncements(tempAnnouncements);
            setBadgeCountAnnouncementsContext(unreadCounter);
          },
          (_error) => {
            // console.log(error);
            setMyAnnouncements([]);
            setBadgeCountAnnouncementsContext(0);
          }
        );

        return () => {
          unsubscribeAnnouncements();
        };
      } catch (error) {
        // console.log(error);
      }
    }
  }, [profile.UserId]);

  React.useEffect(() => {
    if (profile.UserId !== "") {
      try {
        const authoredAnnouncementsQ = query(collection(db, FB_COLLECTION_PATH.Announcements), where("authorUid", "==", profile.UserId));

        const unsubscribeMyAnnouncementPosts = onSnapshot(
          authoredAnnouncementsQ,
          (querySnapshot) => {
            const tempPosts: MyAnnouncementPost[] = [];

            querySnapshot.forEach((docSnap) => {
              const data: any = docSnap.data() || {};

              const postObj: MyAnnouncementPost = {
                announcementId: docSnap.id,
                authorUid: data?.authorUid || "",
                authorName: data?.authorName || "",
                authorCompany: data?.authorCompany || "",
                authorProfilePicURL: data?.authorProfilePicURL || "../assets/profilePic.png",
                title: data?.title || "",
                body: data?.body || "",
                imageUrl: data?.imageUrl ?? null,
                createdAt: Number(data?.createdAt) || 0,
                updatedAt:
                  data?.updatedAt === undefined || data?.updatedAt === null ? undefined : Number(data?.updatedAt) || 0,
                isPinned: data?.isPinned === true,
                status: data?.status || "active",
                recipientUids: Array.isArray(data?.recipientUids) ? data.recipientUids : [],
                selectedCrewIds: Array.isArray(data?.selectedCrewIds) ? data.selectedCrewIds : [],
              };

              if (postObj.status !== "deleted") {
                tempPosts.push(postObj);
              }
            });

            setMyAnnouncementPosts(tempPosts);
          },
          (_error) => {
            // console.log(error);
            setMyAnnouncementPosts([]);
          }
        );

        return () => {
          unsubscribeMyAnnouncementPosts();
        };
      } catch (error) {
        // console.log(error);
      }
    }
  }, [profile.UserId]);

  //
  useEffect(() => {
    if (!currentUser.uid) return;


    const q = query(collection(db, FB_COLLECTION_PATH.Users), where("uid", "==", currentUser.uid));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      querySnapshot.forEach((doc) => {
        const userAttributes = doc.data();

        var subTypeTemp: string = "";
        if (
          userAttributes?.userSubscriptionExpiration === null ||
          userAttributes?.userSubscriptionExpiration === undefined
        ) {
          subTypeTemp = "NONE";
        } else if (userAttributes?.userSubscriptionExpiration < Date.now()) {
          subTypeTemp = "NONE";
        } else {
          subTypeTemp = userAttributes?.userSubscriptionType;
        }

        // assign the profile values to profile obj
        const profileObject = {
          UserId: userAttributes.uid,
          FirstName: userAttributes.firstName,
          LastName: userAttributes.lastName,
          Email: userAttributes.email,
          Username: currentUser.displayName,
          IsScheduler: userAttributes.isScheduler,
          IsDeleted: userAttributes.isDeleted,
          Company: userAttributes.company,
          Occupation: userAttributes.occupation,
          ProfilePicURL: userAttributes.profilePicURL,
          UserBio: userAttributes.userBio,
          DocPath: doc.ref.path,
          Associates: userAttributes.associates_Associates,
          UserLastRoleScheduler: userAttributes.userLastRoleScheduler,
          SubscriptionType: subTypeTemp,
          SubscriptionExpiration: userAttributes?.userSubscriptionExpiration,
          OnboardingWerkerSeen: userAttributes?.onboardingWerkerSeen === true,
          OnboardingSchedulerSeen: userAttributes?.onboardingSchedulerSeen === true,
        };

        setProfile(profileObject);

        if (profileObject.ProfilePicURL === "../assets/profilePic.png") {
          setProfilePicBadge(1);
        } else {
          setProfilePicBadge(0);
        }

        if (profileObject.UserLastRoleScheduler === true) {
          // scheduler color = orange
          setColorTheme("schdbckgrnd");
          setButtonColor("bckgrnd");
        } else {
          // werker color = yellow
          setColorTheme("bckgrnd");
          setButtonColor("warning");
        }
        setUsersID(userAttributes.uid);
      });
    });

    return () => {
      unsubscribe();
    };
    // }, [profile]);
  }, [currentUser]);

  // Badge clearing is now handled in the combined clearAllBadges useEffect above
  // (clears native tile badge + Firestore badgeCount on app open and foreground resume)

  React.useEffect(() => {
    if (profile.UserId !== "") {
      try {
        const notificationsCountRef = doc(db, `users/${profile.UserId}/users_NotificationsCount`, profile.UserId);

        const unsubscribeNotificationsCount = onSnapshot(
          notificationsCountRef,
          (docSnap) => {
            const data: any = docSnap.data() || {};
            const nextCount = Number(data?.notificationsCount) || 0;
            setBadgeCountNotificationsContext(nextCount < 0 ? 0 : nextCount);
          },
          (_error) => {
            // console.log(error);
            setBadgeCountNotificationsContext(0);
          }
        );

        return () => {
          unsubscribeNotificationsCount();
        };
      } catch (error) {
        // console.log(error);
        setBadgeCountNotificationsContext(0);
      }
    } else {
      setBadgeCountNotificationsContext(0);
    }
  }, [profile.UserId]);

  // Payment receipts unread count listener
  React.useEffect(() => {
    if (profile.UserId !== "") {
      try {
        const paymentReceiptsCountRef = doc(db, `users/${profile.UserId}/users_PaymentReceiptsCount`, profile.UserId);

        const unsubscribePaymentReceiptsCount = onSnapshot(
          paymentReceiptsCountRef,
          (docSnap) => {
            const data: any = docSnap.data() || {};
            const nextCount = Number(data?.paymentReceiptsUnreadCount) || 0;
            setBadgeCountPaymentReceiptsContext(nextCount < 0 ? 0 : nextCount);
          },
          () => {
            setBadgeCountPaymentReceiptsContext(0);
          }
        );

        return () => {
          unsubscribePaymentReceiptsCount();
        };
      } catch (error) {
        setBadgeCountPaymentReceiptsContext(0);
      }
    } else {
      setBadgeCountPaymentReceiptsContext(0);
    }
  }, [profile.UserId]);

  useEffect(() => {
    // console.log((packageJson as any).appwebsite);
    try {
      if (profile.UserId !== "") {
        const setupSettingsDoc = async () => {
          const userRef = doc(db, "userSettings", profile.UserId); // Not in FB_COLLECTION_PATH, keep as is
          await setDoc(
            userRef,
            {
              appVersion: packageJson.version,
              userID: profile.UserId,
            },
            { merge: true }
          ).then(() => {
            // console.log("updated my app version");
          });
        };
        setupSettingsDoc();
      }
    } catch (error) {
      // console.log(error);
    }
  }, [profile]);

  const [currentAppVersions, setCurrentAppVersions] = useState<any[]>([]);
  const [forceUpdate, setForceUpdate] = useState(false);

  // Listen only to the admin feature-flag doc for the active version list.
  // We compare against packageJson.version (known synchronously at runtime)
  // rather than re-reading our own appVersion from Firestore, which avoids
  // stale-cache / write-listener race conditions that produced spurious
  // "please update" popups.
  useEffect(() => {
    try {
      const flagDocRef = doc(db, "userSettings", "00FeatureFlagSettings"); // Not in FB_COLLECTION_PATH, keep as is
      const unsubscribe4 = onSnapshot(flagDocRef, (snap) => {
        const data = snap.data();
        if (data && Array.isArray(data.activeAppVersions)) {
          setCurrentAppVersions(data.activeAppVersions);
        }
      });
      return () => {
        unsubscribe4();
      };
    } catch (error) {
      // console.log(error);
    }
  }, []);

  useEffect(() => {
    // Guard: wait until the admin list has loaded before evaluating.
    if (currentAppVersions.length === 0) return;
    const myVersion = String(packageJson.version).trim();
    const inList = currentAppVersions.some((v) => String(v).trim() === myVersion);
    setForceUpdate(!inList);
  }, [currentAppVersions]);

  // RevenueCat - Check User's Subscription
  useEffect(() => {
    const RevenueCatFX = async () => {
      try {
        if (Capacitor.getPlatform() === "ios") {
          Sentry.addBreadcrumb({ category: "subscription", message: "Configuring RevenueCat (iOS)", level: "info" });
          await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });
          await Purchases.configure({
            apiKey: "appl_iMWtgbqUuMgwKiPchGQmfthxxRc",
            appUserID: profile.UserId, // Optional
          });
        }
        if (Capacitor.getPlatform() === "android") {
          Sentry.addBreadcrumb({ category: "subscription", message: "Configuring RevenueCat (Android)", level: "info" });
          await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });
          await Purchases.configure({
            apiKey: "goog_qnZCUjgYjODmYqsdQefMSwiVFRc",
            appUserID: profile.UserId, // Optional
          });
        }
        if (Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "android") {
          Sentry.addBreadcrumb({ category: "subscription", message: "Fetching customer info from RevenueCat", level: "info" });
          const entitlementsList = await Purchases.getCustomerInfo();
          // console.log("entitlements, ", entitlementsList.customerInfo.entitlements.active);

          let schedulerGranted = false;
          let werkerGranted = false;

          if (entitlementsList.customerInfo.entitlements.active["Werker Subscription"]) {
            // console.log("found a Werker Subscription");
            var subDate: number = 0;
            if (
              entitlementsList.customerInfo.entitlements.active["Werker Subscription"].expirationDateMillis !== null
            ) {
              subDate = entitlementsList.customerInfo.entitlements.active["Werker Subscription"].expirationDateMillis;
            }
            await updateProfileDocSubStatus(false, "Werker", subDate);
            werkerGranted = true;
          }

          if (entitlementsList.customerInfo.entitlements.active["Scheduler Subscription"]) {
            // console.log("found a Scheduler Subscription");
            var subDate2: number = 0;
            if (
              entitlementsList.customerInfo.entitlements.active["Scheduler Subscription"].expirationDateMillis !== null
            ) {
              subDate2 =
                entitlementsList.customerInfo.entitlements.active["Scheduler Subscription"].expirationDateMillis;
            }
            await updateProfileDocSubStatus(true, "Scheduler", subDate2);
            schedulerGranted = true;
          }

          // Check for comped Scheduler access via feature flag document
          const flagDocSnap = await getDoc(doc(db, "userSettings", "00FeatureFlagSettings"));
          const compedSchedulers: string[] = flagDocSnap.data()?.compedSchedulers ?? [];
          if (compedSchedulers.includes(profile.UserId)) {
            await updateProfileDocSubStatus(true, "Scheduler", COMP_SCHEDULER_EXPIRY);
            schedulerGranted = true;
          }

          // Revoke access if neither RC nor comp granted any subscription
          if (!schedulerGranted && !werkerGranted) {
            await updateProfileDocSubStatus(false, "Werker", 0);
          }
        } else {
          // accessing thru the browser
          updateProfileDocSubStatus(true, "Werker", Date.now() + 10000);
          updateProfileDocSubStatus(true, "Scheduler", Date.now() + 10000);
        }
      } catch (error) {
        console.error("[RevenueCat] Error during subscription check:", error);
        Sentry.captureException(error);
      }
    };

    const updateProfileDocSubStatus = async (schedStatus: boolean, subType: string, subExpireDate: number) => {
      const docSubscriptionStatus = doc(db, FB_COLLECTION_PATH.Users, profile.UserId);
      await updateDoc(docSubscriptionStatus, {
        isScheduler: schedStatus,
        userSubscriptionType: subType,
        userSubscriptionExpiration: subExpireDate,
        // userLastRoleScheduler is intentionally excluded here — it is a user
        // role preference controlled by the toggle and should not be overwritten
        // by the subscription check.
      })
        .then(async () => {
          const docSubscriptionStatus2 = doc(db, FB_COLLECTION_PATH.Users + "/" + profile.UserId + "/" + FB_COLLECTION_PATH.UsersPublicFacing, profile.UserId);
          await setDoc(docSubscriptionStatus2, {
            isScheduler: schedStatus,
          }, { merge: true });
        })
        .then(() => {
          return;
        });
    };

    if (profile.UserId !== "") {
      // console.log("running RevenueCatFX()");
      RevenueCatFX();
    }
  }, [profile.UserId, subscriptionRefreshCtr]);

  // Operation status listener for async job operations (edit, invite)
  React.useEffect(() => {
    if (!profile.UserId) return;

    const statusQuery = query(
      collection(db, `users/${profile.UserId}/operationStatus`)
    );

    const unsubscribe = onSnapshot(
      statusQuery,
      async (querySnapshot) => {
        querySnapshot.forEach(async (statusDoc) => {
          const data: any = statusDoc.data();
          const { operationType, status, message, error } = data;

          // Show toast only on failure
          if (status === "error") {
            await presentToast({
              message: message || "Operation failed. Please try again.",
              duration: 4000,
              position: "bottom",
              color: "danger",
            });
            
            // Log error for debugging
            if (error) {
              console.error(`${operationType} error:`, error);
            }
          }

          // Delete status doc after processing
          try {
            await deleteDoc(statusDoc.ref);
          } catch (deleteError) {
            console.error("Error deleting operation status doc:", deleteError);
          }
        });
      },
      (error) => {
        console.error("Error listening to operation status:", error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [profile.UserId, presentToast]);

  const subscriptionReducer = (state: any, action: { type: any; payload: any }) => {
    switch (action.type) {
      case "REFRESH_SUBSCRIPTION":
        // console.log("Refreshing Subscription");
        setSubscriptionRefreshCtr(subscriptionRefreshCtr + 1);
        return { return: "refreshed" };
      default:
        return state;
    }
  };
  const [, subscriptionDispatch] = useReducer(subscriptionReducer, 0);

  const profileContextValue = React.useMemo(() => ({
    profile,
    profilePicBadge,
    networkConnection,
    colorTheme,
    buttonColor,
    usersID,
    myAssociates,
    myChats,
    myRequests,
    everyoneElse,
    badgeCountAssociateRequestsContext,
    badgeCountDMContext,
    badgeCountNotificationsContext,
    badgeCountPaymentReceiptsContext,
    forceUpdate,
    subscriptionDispatch,
    refreshEveryoneElse,

    myAnnouncements,
    myAnnouncementPosts,
    badgeCountAnnouncementsContext,

    allowedJobIdsWerker,
    allowedJobIdsScheduler,
    allowedJobIdsUnion,
    jobFilteringReady,
    jobRouteByJobId,
    cancelledBySchedulerWerker,
    cancelledBySchedulerScheduler,
  }), [
    profile,
    profilePicBadge,
    networkConnection,
    colorTheme,
    buttonColor,
    usersID,
    myAssociates,
    myChats,
    myRequests,
    everyoneElse,
    badgeCountAssociateRequestsContext,
    badgeCountDMContext,
    badgeCountNotificationsContext,
    badgeCountPaymentReceiptsContext,
    forceUpdate,
    subscriptionDispatch,
    refreshEveryoneElse,
    myAnnouncements,
    myAnnouncementPosts,
    badgeCountAnnouncementsContext,
    allowedJobIdsWerker,
    allowedJobIdsScheduler,
    allowedJobIdsUnion,
    jobFilteringReady,
    jobRouteByJobId,
    cancelledBySchedulerWerker,
    cancelledBySchedulerScheduler,
  ]);

  return (
    <ProfileContext.Provider value={profileContextValue}>
      {children}
    </ProfileContext.Provider>
  );
};
